/**
 * DonutChart Component
 * Simple SVG-based donut chart for status distribution
 */

import React from 'react';

interface DonutChartData {
  label: string;
  value: number;
  color: string;
}

interface DonutChartProps {
  data: DonutChartData[];
  size?: number;
  strokeWidth?: number;
  showLegend?: boolean;
  showCenter?: boolean;
  centerLabel?: string;
  centerValue?: string | number;
  className?: string;
  legendColumns?: number;
  legendPosition?: 'right' | 'bottom' | 'responsive';
  compactLegend?: boolean;
}

const DonutChart: React.FC<DonutChartProps> = ({
  data,
  size = 180,
  strokeWidth = 30,
  showLegend = true,
  showCenter = true,
  centerLabel,
  centerValue,
  className = '',
  legendColumns,
  legendPosition = 'right',
  compactLegend = false,
}) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  let accumulatedPercentage = 0;

  const segments = data.map((item) => {
    const percentage = total > 0 ? (item.value / total) * 100 : 0;
    const strokeDasharray = `${(percentage / 100) * circumference} ${circumference}`;
    const rotation = (accumulatedPercentage / 100) * 360;
    accumulatedPercentage += percentage;

    return {
      ...item,
      percentage,
      strokeDasharray,
      rotation,
    };
  });

  const legendGrid = legendColumns
    ? { gridTemplateColumns: `repeat(${legendColumns}, minmax(0, 1fr))` }
    : undefined;

  const legendItems = segments.map((segment, index) => (
    <div key={index} className={`flex items-center ${compactLegend ? 'gap-1' : 'gap-2'}`}>
      <div
        className={`${compactLegend ? 'w-2 h-2' : 'w-3 h-3'} rounded-full flex-shrink-0`}
        style={{ backgroundColor: segment.color }}
      />
      <div className={`flex items-center ${compactLegend ? 'gap-1' : 'gap-2'} min-w-0`}>
        <span className={`${compactLegend ? 'text-[10px]' : 'text-sm'} text-gray-700 dark:text-gray-300 truncate`}>{segment.label}</span>
        <span className={`${compactLegend ? 'text-[10px]' : 'text-sm'} font-semibold text-gray-900 dark:text-gray-100`}>{segment.value}</span>
        <span className={`${compactLegend ? 'text-[9px]' : 'text-xs'} text-gray-400 dark:text-gray-500 whitespace-nowrap`}>({segment.percentage.toFixed(1)}%)</span>
      </div>
    </div>
  ));

  const chart = (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={center} cy={center} r={radius} fill="none" className="stroke-gray-100 dark:stroke-gray-700" strokeWidth={strokeWidth} />
        {segments.map((segment, index) => (
          <circle
            key={index}
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={segment.color}
            strokeWidth={strokeWidth}
            strokeDasharray={segment.strokeDasharray}
            strokeDashoffset={0}
            style={{
              transform: `rotate(${segment.rotation}deg)`,
              transformOrigin: 'center',
              transition: 'stroke-dasharray 0.5s ease-out',
            }}
          />
        ))}
      </svg>
      {showCenter && (
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {centerValue !== undefined && (
            <span className="text-2xl font-bold text-gray-800 dark:text-gray-100">{centerValue}</span>
          )}
          {centerLabel && (
            <span className="text-xs text-gray-500 dark:text-gray-400">{centerLabel}</span>
          )}
        </div>
      )}
    </div>
  );

  // Responsive: right on lg+, bottom on smaller screens
  if (legendPosition === 'responsive' && showLegend) {
    return (
      <div className={className}>
        {/* Desktop: side by side */}
        <div className="hidden lg:flex items-center gap-6">
          {chart}
          <div className={legendGrid ? 'grid gap-2' : 'flex flex-col gap-2'} style={legendGrid}>
            {legendItems}
          </div>
        </div>
        {/* Mobile/Tablet: stacked */}
        <div className="flex lg:hidden flex-col items-center gap-4">
          {chart}
          <div className={legendGrid ? 'grid gap-2' : 'flex flex-col gap-2'} style={legendGrid}>
            {legendItems}
          </div>
        </div>
      </div>
    );
  }

  const isBottom = legendPosition === 'bottom';

  return (
    <div className={`${isBottom ? 'flex flex-col items-center gap-4' : 'flex items-center gap-6'} ${className}`}>
      {chart}
      {showLegend && (
        <div className={legendGrid ? 'grid gap-2' : 'flex flex-col gap-2'} style={legendGrid}>
          {legendItems}
        </div>
      )}
    </div>
  );
};

export default DonutChart;
